"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HiSparkles } from "react-icons/hi2";
import ResultsSection from "@/components/skin-analysis/results-section";
import { Button } from "@/components/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { queryClient } from "@/config/react-query";
import { toast } from "@/lib/use-toast";
import { getAttachmentSkinAnalysis, runAttachmentSkinAnalysis } from "@/api/provider/scribe/skin-analysis";
interface SkinAnalysisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachmentId: string | null;
}
export default function SkinAnalysisDialog(t0) {
  const $ = _c(40);
  const {
    open,
    onOpenChange,
    attachmentId
  } = t0;
  let t1;
  if ($[0] !== attachmentId) {
    t1 = ["skin-analysis", attachmentId];
    $[0] = attachmentId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryKey = t1;
  let t2;
  if ($[2] !== attachmentId) {
    t2 = () => getAttachmentSkinAnalysis(attachmentId);
    $[2] = attachmentId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = open && !!attachmentId;
  let t4;
  if ($[4] !== queryKey || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey,
      queryFn: t2,
      enabled: t3,
      retry: false,
      staleTime: 120000
    };
    $[4] = queryKey;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: result,
    isLoading: fetching
  } = useQuery(t4);
  let t5;
  if ($[8] !== attachmentId) {
    t5 = () => runAttachmentSkinAnalysis(attachmentId);
    $[8] = attachmentId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== queryKey) {
    t6 = data => {
      queryClient.setQueryData(queryKey, data);
      toast({
        title: "Skin Analysis Complete",
        variant: "success"
      });
    };
    $[10] = queryKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = {
      mutationFn: t5,
      onSuccess: t6,
      onError: _temp
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const mutation = useMutation(t7);
  let t8;
  if ($[15] !== result) {
    t8 = result ? {
      overallSkinAge: result.overallSkinAge,
      overallScore: result.overallScore,
      ageDifference: result.ageDifference,
      summary: result.summary,
      zones: result.zones,
      concerns: result.concerns,
      recommendations: result.recommendations,
      imagePath: ""
    } : null;
    $[15] = result;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const resultForDisplay = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogHeader><DialogTitle className="flex items-center gap-2"><HiSparkles className="h-5 w-5 text-primary" />Skin Analysis</DialogTitle></DialogHeader>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== fetching || $[19] !== result) {
    t10 = fetching && !result && <div className="flex items-center justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
    $[18] = fetching;
    $[19] = result;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== fetching || $[22] !== mutation || $[23] !== result) {
    t11 = !fetching && !result && !mutation.isPending && <div className="space-y-4 py-8 text-center"><div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10"><HiSparkles className="h-8 w-8 text-primary" /></div><div><p className="text-body text-foreground-secondary">Run AI-powered skin analysis on this photo to assess skin age across 5 facial zones.</p></div><Button variant="ai" onClick={() => mutation.mutate()}>Run Skin Analysis</Button></div>;
    $[21] = fetching;
    $[22] = mutation;
    $[23] = result;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== mutation.isPending) {
    t12 = mutation.isPending && <div className="space-y-4 py-12 text-center"><div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /><p className="text-body-strong text-foreground-secondary">Analyzing skin... this may take a few seconds</p></div>;
    $[25] = mutation.isPending;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== mutation || $[28] !== result || $[29] !== resultForDisplay) {
    t13 = result && resultForDisplay && !mutation.isPending && <div className="space-y-4"><ResultsSection result={resultForDisplay} /><div className="flex justify-center pb-2"><Button variant="secondary" size="sm" onClick={() => mutation.mutate()}>Re-run Analysis</Button></div></div>;
    $[27] = mutation;
    $[28] = result;
    $[29] = resultForDisplay;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <DialogContent className="max-w-2xl">{t9}<div className="max-h-[70vh] overflow-y-auto px-1">{t10}{t11}{t12}{t13}</div></DialogContent>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== onOpenChange || $[37] !== open || $[38] !== t14) {
    t15 = <Dialog open={open} onOpenChange={onOpenChange}>{t14}</Dialog>;
    $[36] = onOpenChange;
    $[37] = open;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}
function _temp() {
  toast({
    title: "Skin analysis failed",
    description: "Please try again.",
    variant: "error"
  });
}